import { useEffect, useState, useCallback } from 'react'
import { TamaguiProvider, YStack } from 'tamagui'

import config from './tamagui.config'
import { initAnalytics, openBookingPage } from './lib/analytics'
import {
  HeroSection,
  LogoBarSection,
  ProblemAgitateSection,
  CaseStudySection,
  SocialProofSection,
  TeamSection,
  TransformationSection,
  FAQSection,
  SecondaryCTASection,
  FooterSection,
} from './components/sections'
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage'
import { TermsOfServicePage } from './pages/TermsOfServicePage'
import { CoachingPage } from './pages/CoachingPage'
import { DeckGate, DeckViewer } from './components/deck'
import { getDeck } from './content/decks'
import type { Deck } from './content/decks/types'

interface DeckRoute {
  workshopSlug: string
  deckType?: string
  slide?: number
}

function parseDeckRoute(pathname: string): DeckRoute | null {
  const match = pathname.match(
    /^\/workshops\/([^/]+)(?:\/([^/]+))?(?:\/(\d+))?$/
  )
  if (!match) return null
  return {
    workshopSlug: match[1],
    deckType: match[2],
    slide: match[3] ? parseInt(match[3], 10) : undefined,
  }
}

function SpeakeasyPage({ route }: { route: DeckRoute }) {
  const [state, setState] = useState<
    | { phase: 'gate' }
    | { phase: 'viewing'; deck: Deck; viewId: string; initialSlide: number }
  >(() => {
    try {
      const stored = sessionStorage.getItem('deck-auth')
      if (stored) {
        sessionStorage.removeItem('deck-auth')
        const { viewId } = JSON.parse(stored)
        const deck = getDeck(route.workshopSlug, route.deckType || 'main')
        if (deck && viewId) {
          return { phase: 'viewing', deck, viewId, initialSlide: route.slide ?? 0 }
        }
      }
    } catch {}
    return { phase: 'gate' }
  })

  const handleAuthenticated = useCallback(
    (result: {
      deck: Deck
      attendeeName?: string
      accessCodeId: string
      viewId: string
      workshopSlug: string
    }) => {
      setState({
        phase: 'viewing',
        deck: result.deck,
        viewId: result.viewId,
        initialSlide: route.slide ?? 0,
      })
    },
    [route.slide]
  )

  const handleClose = useCallback(() => {
    window.location.href = '/'
  }, [])

  if (state.phase === 'gate') {
    return (
      <YStack flex={1} backgroundColor="$background">
        <DeckGate
          workshopSlug={route.workshopSlug}
          onAuthenticated={handleAuthenticated}
          onClose={handleClose}
          requestedDeckType={route.deckType}
        />
      </YStack>
    )
  }

  return (
    <DeckViewer
      deck={state.deck}
      workshopSlug={route.workshopSlug}
      viewId={state.viewId}
      initialSlide={state.initialSlide}
      onClose={handleClose}
    />
  )
}

function Home() {
  const [gateOpen, setGateOpen] = useState(false)

  // Cmd+K opens the secure access gate
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setGateOpen(true)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Logo tap opens the gate (dispatched from HeroSection)
  useEffect(() => {
    const handleLogoTap = () => setGateOpen(true)
    window.addEventListener('open-secure-gate', handleLogoTap)
    return () => window.removeEventListener('open-secure-gate', handleLogoTap)
  }, [])

  const handleAuthenticated = useCallback(
    (result: {
      deck: Deck
      attendeeName?: string
      accessCodeId: string
      viewId: string
      workshopSlug: string
    }) => {
      setGateOpen(false)
      sessionStorage.setItem('deck-auth', JSON.stringify({
        viewId: result.viewId,
        accessCodeId: result.accessCodeId,
      }))
      window.location.href = `/workshops/${result.workshopSlug}/${result.deck.type}/0`
    },
    []
  )

  const handleCloseGate = useCallback(() => {
    setGateOpen(false)
  }, [])

  return (
    <YStack flex={1} backgroundColor="$background">
      <HeroSection onCtaClick={() => openBookingPage('Hero')} />
      <LogoBarSection />
      <ProblemAgitateSection />
      <CaseStudySection />
      <SocialProofSection />
      <TeamSection />
      <TransformationSection />
      <SecondaryCTASection
        onCtaClick={() => openBookingPage('SecondaryCta')}
      />
      <FAQSection />
      <FooterSection />
      {gateOpen && (
        <DeckGate onAuthenticated={handleAuthenticated} onClose={handleCloseGate} />
      )}
    </YStack>
  )
}

const renderPage = () => {
  const path =
    typeof window === 'undefined'
      ? '/'
      : window.location.pathname.replace(/\/+$/, '') || '/'

  switch (path) {
    case '/privacy-policy':
      return <PrivacyPolicyPage />
    case '/terms-of-service':
      return <TermsOfServicePage />
    case '/blueprint':
      return <CoachingPage />
    default: {
      const deckRoute = parseDeckRoute(path)
      if (deckRoute) {
        return <SpeakeasyPage route={deckRoute} />
      }
      return <Home />
    }
  }
}

export const Root = () => {
  useEffect(() => {
    initAnalytics()
  }, [])

  return (
    <TamaguiProvider config={config} defaultTheme="dark">
      {renderPage()}
    </TamaguiProvider>
  )
}
